import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputAdornment,
    Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import {
    useCreateEmissionTarget,
    useUpdateEmissionTarget,
    useGetAllowedValues,
    useNotify,
} from "hooks";
import {
    EsgSingleSelectControlledField,
    EsgDatePickerField,
    TextField as FormTextField,
    COLORS,
} from "components";
import {
    ANNUAL_PATH_OPTIONS,
    EMISSIONS_SCOPES,
    TARGET_TYPES,
    type EditTargetData,
    type TargetFormData,
    type TargetLevel,
} from "./types";
import { formDataToRequest, getYearsBetween, computeEvenAnnualReduction } from "./helpers";
import {
    defaultFormValues,
    LEVEL_OPTIONS,
    ANNUAL_PATH_RADIO_OPTIONS,
} from "./consts";
import { fieldLabelSx, dialogPaperSx, dialogTitleSx, dialogContentSx, placeholderSx, requiredFieldSx } from "./styles";
import { useEntitySelect } from "./hooks";
import {
    EntitySelectField,
    TargetTypeField,
    CustomAnnualTable,
    LabelWithTooltip,
    InfoMessageBox,
} from "./components";

export interface AddTargetModalProps {
    isAddTargetModalOpen: boolean;
    onAddTargetModalClose: () => void;
    /** When provided, modal shows "Edit Target" and form is pre-filled */
    editTarget?: EditTargetData | null;
}

export const AddTargetModal = ({
    isAddTargetModalOpen,
    onAddTargetModalClose,
    editTarget,
}: AddTargetModalProps) => {
    const notify = useNotify();
    const { data: allowedValues } = useGetAllowedValues();
    const { mutateAsync: createTarget } = useCreateEmissionTarget();
    const { mutateAsync: updateTarget } = useUpdateEmissionTarget();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditMode = !!editTarget;

    const { control, handleSubmit, reset, watch, setValue } = useForm<TargetFormData>({
        mode: "onChange",
        defaultValues: defaultFormValues,
    });

    const targetLevel = watch("targetLevel") as TargetLevel | "";
    const targetType = watch("targetType");
    const baselineYear = watch("baselineYear");
    const targetYear = watch("targetYear");
    const targetValue = watch("targetValue");
    const annualTargetPath = watch("annualTargetPath");
    const entity = watch("entity");
    const emissionsScope = watch("emissionsScope");

    const entitySelectState = useEntitySelect(targetLevel, allowedValues);

    const baselineYearNum = baselineYear
        ? (typeof baselineYear === "string" ? parseInt(baselineYear, 10) : dayjs(baselineYear).year())
        : null;
    const targetYearNum = targetYear
        ? (typeof targetYear === "string" ? parseInt(targetYear, 10) : dayjs(targetYear).year())
        : null;
    const targetValueNum = targetValue ? parseFloat(targetValue) : NaN;

    const yearsBetween = useMemo(() => {
        if (baselineYearNum == null || targetYearNum == null || baselineYearNum >= targetYearNum) return [];
        return getYearsBetween(baselineYearNum, targetYearNum);
    }, [baselineYearNum, targetYearNum]);

    const evenAnnualReductionLabel = useMemo(() => {
        if (baselineYearNum == null || targetYearNum == null || !Number.isFinite(targetValueNum)) return "—";
        return computeEvenAnnualReduction(targetType, baselineYearNum, targetYearNum, targetValueNum);
    }, [targetType, baselineYearNum, targetYearNum, targetValueNum]);

    const isFormValid = useMemo(() => {
        if (!targetLevel || !emissionsScope?.trim()) return false;
        if (targetLevel !== "Corporate" && !entity?.trim()) return false;
        if (!baselineYear || !targetYear) return false;
        const baseNum = typeof baselineYear === "string" ? parseInt(baselineYear, 10) : dayjs(baselineYear).year();
        const targetNum = typeof targetYear === "string" ? parseInt(targetYear, 10) : dayjs(targetYear).year();
        if (!Number.isInteger(baseNum) || !Number.isInteger(targetNum) || baseNum >= targetNum) return false;
        const value = parseFloat(targetValue);
        if (targetValue?.trim() === "" || !Number.isFinite(value)) return false;
        return true;
    }, [targetLevel, entity, emissionsScope, baselineYear, targetYear, targetValue]);

    useEffect(() => {
        if (!isAddTargetModalOpen) return;
        if (editTarget) {
            reset({
                targetLevel: editTarget.targetLevel,
                entity: editTarget.entity,
                targetType: editTarget.targetType,
                emissionsScope: editTarget.emissionsScope,
                baselineYear: editTarget.baselineYear ? dayjs().year(editTarget.baselineYear).format("YYYY") : null,
                targetYear: editTarget.targetYear ? dayjs().year(editTarget.targetYear).format("YYYY") : null,
                targetValue: String(editTarget.targetValue),
                annualTargetPath: editTarget.annualTargetPath,
                customAnnualValues: editTarget.customAnnualValues
                    ? Object.fromEntries(Object.entries(editTarget.customAnnualValues).map(([k, v]) => [k, String(v)]))
                    : {},
                notes: editTarget.notes ?? "",
            });
        } else {
            reset(defaultFormValues);
        }
    }, [isAddTargetModalOpen, editTarget, reset]);

    const prevTargetLevelRef = useRef<string | null>(null);
    useEffect(() => {
        if (targetLevel) {
            const prev = prevTargetLevelRef.current;
            if (prev != null && prev !== "" && prev !== targetLevel) {
                setValue("entity", "");
                entitySelectState.reset();
            }
            prevTargetLevelRef.current = targetLevel;
        } else {
            prevTargetLevelRef.current = null;
        }
    }, [targetLevel, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (editTarget) return; // keep pre-filled target year when editing
        if (!baselineYear && targetYear) {
            setValue("targetYear", null);
        } else if (baselineYearNum != null && targetYearNum != null && targetYearNum <= baselineYearNum) {
            setValue("targetYear", null);
        }
    }, [editTarget, baselineYear, baselineYearNum, targetYear, targetYearNum, setValue]);

    const handleClose = () => {
        entitySelectState.reset();
        reset(defaultFormValues);
        onAddTargetModalClose();
    };

    const onSubmit = async (data: TargetFormData) => {
        const request = formDataToRequest(data);
        if (!request) {
            notify.error("Please fill in all required fields and ensure Entity is selected.");
            return;
        }
        setIsSubmitting(true);
        try {
            if (isEditMode && editTarget) {
                await updateTarget({ id: editTarget.id, body: request });
                notify.success("Target updated successfully.");
            } else {
                await createTarget(request);
                notify.success("Target created successfully.");
            }
            handleClose();
        } catch (err: unknown) {
            const message =
                typeof (err as { response?: { data?: string } })?.response?.data === "string"
                    ? (err as { response: { data: string } }).response.data
                    : "Failed to save target.";
            notify.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalTitle = isEditMode ? "Edit Target" : "Add Target";

    return (
        <Dialog open={isAddTargetModalOpen} onClose={handleClose} maxWidth={false} sx={dialogPaperSx}>
            <DialogTitle sx={dialogTitleSx}>{modalTitle}</DialogTitle>

            <DialogContent sx={dialogContentSx}>
                <form onSubmit={handleSubmit(onSubmit)} id="add-target-form">
                    <Grid container spacing={2}>
                        {/* Row 1: Target Level + Entity */}
                        <Grid item xs={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <EsgSingleSelectControlledField
                                    label="Target Level"
                                    selectedValue={targetLevel}
                                    setSelectedValue={(v) => setValue("targetLevel", v as TargetLevel | "")}
                                    options={LEVEL_OPTIONS}
                                    placeholder="Select Level"
                                    width="100%"
                                    required
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <EntitySelectField
                                targetLevel={targetLevel}
                                entity={entity}
                                onEntityChange={(v) => setValue("entity", v)}
                                entityState={entitySelectState}
                            />
                        </Grid>

                        {/* Row 2: Target Type */}
                        <Grid item xs={12}>
                            <TargetTypeField control={control} />
                        </Grid>

                        {/* Row 3: Emissions Scope */}
                        <Grid item xs={12}>
                            <EsgSingleSelectControlledField
                                label="Emissions Scope"
                                selectedValue={emissionsScope}
                                setSelectedValue={(v) => setValue("emissionsScope", v as string)}
                                options={[...EMISSIONS_SCOPES]}
                                placeholder="Select Scope"
                                width="100%"
                                required
                            />
                        </Grid>

                        {/* Row 4: Baseline Year, Target Year, Target value */}
                        <Grid item xs={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <LabelWithTooltip
                                    title="Baseline Year"
                                    tooltipMessage="Reductions are calculated relative to emissions recorded in Tracera for the selected baseline year."
                                    required
                                />
                                <Controller
                                    name="baselineYear"
                                    control={control}
                                    render={({ field }) => (
                                        <EsgDatePickerField
                                            value={field.value ? dayjs(field.value, "YYYY") : null}
                                            onChange={(date: Dayjs | null) => field.onChange(date ? date.format("YYYY") : null)}
                                            format="YYYY"
                                            views={["year"]}
                                            width="100%"
                                            placeholdervalue="YYYY"
                                            required
                                            placeholderSx={placeholderSx}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <Typography sx={fieldLabelSx}>
                                    Target Year<Typography component="span" sx={requiredFieldSx}> *</Typography>
                                </Typography>
                                <Controller
                                    name="targetYear"
                                    control={control}
                                    render={({ field }) => (
                                        <EsgDatePickerField
                                            value={field.value ? dayjs(field.value, "YYYY") : null}
                                            onChange={(date: Dayjs | null) => field.onChange(date ? date.format("YYYY") : null)}
                                            format="YYYY"
                                            views={["year"]}
                                            width="100%"
                                            placeholdervalue="YYYY"
                                            required
                                            disabled={isEditMode ? false : !baselineYear}
                                            minDate={
                                                baselineYearNum != null
                                                    ? dayjs().year(baselineYearNum + 1).startOf("year")
                                                    : undefined
                                            }
                                            placeholderSx={placeholderSx}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                <Typography sx={fieldLabelSx}>
                                    Target value<Typography component="span" sx={requiredFieldSx}> *</Typography>
                                </Typography>
                                <Controller
                                    name="targetValue"
                                    control={control}
                                    render={({ field }) => (
                                        <FormTextField
                                            {...field}
                                            size="small"
                                            placeholder="e.g 50"
                                            sx={{
                                                width: "100%",
                                                "& .MuiInputBase-root": {
                                                    height: "36px",
                                                    fontSize: "16px",
                                                    borderRadius: "8px",
                                                },
                                                "& .MuiInputBase-input::placeholder": {
                                                    ...placeholderSx,
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ position: "absolute", right: 12 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {targetType === TARGET_TYPES.PERCENT_REDUCTION ? "%" : " tCO₂e"}
                                                        </Typography>
                                                    </InputAdornment>
                                                ),
                                                inputProps: {
                                                    style: {
                                                        paddingRight: targetType === TARGET_TYPES.PERCENT_REDUCTION ? 28 : 56,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ mt: 2, mb: 1 }} />

                    {/* Annual Target Path */}
                    <Typography sx={{ fontSize: "12px", color: "black", fontWeight: 600, mb: "4px" }}>
                        Annual Target Path
                        <Typography component="span" sx={requiredFieldSx}> *</Typography>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: '12px' }}>
                        {ANNUAL_PATH_RADIO_OPTIONS.map((opt) => (
                            <Controller
                                key={opt.value}
                                name="annualTargetPath"
                                control={control}
                                render={({ field }) => (
                                    <Box component="label" sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            checked={field.value === opt.value}
                                            onChange={() => field.onChange(opt.value)}
                                            style={{ marginRight: 8 }}
                                        />
                                        <Typography sx={{ fontSize: "16px", fontWeight: 400, opacity: 0.9 }}>{opt.label}</Typography>
                                    </Box>
                                )}
                            />
                        ))}
                    </Box>

                    {annualTargetPath === ANNUAL_PATH_OPTIONS.EVEN && (
                        <InfoMessageBox>
                            <Typography variant="body2" color={COLORS.Romance}>
                                Based on your inputs, the annual reduction will be approximately {evenAnnualReductionLabel}{" "}
                                per year.
                            </Typography>
                        </InfoMessageBox>
                    )}

                    {annualTargetPath === ANNUAL_PATH_OPTIONS.CUSTOM && (
                        <Box sx={{ mb: "12px" }}>
                            {yearsBetween.length === 0 ? (
                                <InfoMessageBox>
                                    <Typography variant="body2" color={COLORS.Romance}>
                                        Please select Baseline Year and Target Year to define custom annual values.
                                    </Typography>
                                </InfoMessageBox>
                            ) : (
                                <>
                                    <InfoMessageBox>
                                        <Box>
                                            <Typography variant="body2" color={COLORS.Romance}>
                                                {targetType === TARGET_TYPES.PERCENT_REDUCTION
                                                    ? "Enter the annual percentage reduction relative to the baseline emissions. Values represent the total reduction achieved by that year compared to the baseline year (not the year-over-year change). Reductions are assumed to decrease emissions and should be entered as positive percentages."
                                                    : "Enter the absolute emissions target (tCO₂e) for each year. Values represent the total emissions for that year."}
                                            </Typography>
                                        </Box>
                                    </InfoMessageBox>
                                    <CustomAnnualTable yearsBetween={yearsBetween} control={control} targetType={targetType} />
                                </>
                            )}
                        </Box>
                    )}

                    {/* Notes (optional) */}
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: "12px", color: "black", fontWeight: 600, mb: 0.5 }}>
                            Notes (optional)
                        </Typography>
                        <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                                <FormTextField
                                    {...field}
                                    size="small"
                                    multiline
                                    rows={3}
                                    placeholder="Add any additional context or methodology notes regarding this target..."
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {
                                            height: "auto",
                                            fontSize: "16px",
                                            borderRadius: "4px",
                                        },
                                        "& .MuiInputBase-input::placeholder": {
                                            ...placeholderSx,
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </form>
            </DialogContent>

            <DialogActions sx={{ px: "24px", pb: 2 }}>
                <Box sx={{ display: "flex", gap: "12px" }}>
                    <Button onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="add-target-form"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !isFormValid}
                    >
                        {isSubmitting ? "Saving..." : "Save Target"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
